import React, { useState, useEffect } from 'react';
import {
  X, User, Image, FileText, Video, Grid,
  Youtube, Instagram, Twitter, Linkedin, Facebook, Plus,
  Upload, Link,
  CheckCircle, Info, Sparkles, Target, Hash, Calendar
} from 'lucide-react';
import { useCalendarStore, ContentItem, PlatformName, ScheduleStatus, CreateContentItemData, UpdateContentItemData } from '../../store/calendarStore';

interface AddContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  content?: ContentItem | null;
}

type FormErrors = {
  [K in keyof CreateContentItemData]?: string;
};

const AddContentModal: React.FC<AddContentModalProps> = ({ isOpen, onClose, content, onSuccess }) => {
  const { createContent, updateContent } = useCalendarStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [showTips, setShowTips] = useState(true);

  const [formData, setFormData] = useState<CreateContentItemData>({
    title: '',
    description: '',
    platform: 'youtube',
    status: 'draft',
    scheduledDate: new Date().toISOString().split('T')[0],
    scheduledTime: '09:00',
    assignedTo: '',
    assignedToName: '',
    tags: [],
    thumbnailUrl: '',
    autoPublish: false,
    notes: '',
    contentType: 'video',
    checklist: []
  });

  const [errors, setErrors] = useState<Partial<FormErrors>>({});

  useEffect(() => {
    if (content) {
      setFormData({
        title: content.title,
        description: content.description,
        platform: content.platform,
        status: content.status,
        scheduledDate: content.scheduledDate.toISOString().split('T')[0],
        scheduledTime: content.scheduledTime || '09:00',
        assignedTo: content.assignedTo || '',
        assignedToName: content.assignedToName || '',
        tags: [...content.tags],
        thumbnailUrl: content.thumbnailUrl || '',
        autoPublish: content.autoPublish,
        notes: content.notes || '',
        contentType: content.contentType,
        checklist: [...content.checklist]
      });
    } else {
      setFormData({
        title: '',
        description: '',
        platform: 'youtube',
        status: 'draft',
        scheduledDate: new Date().toISOString().split('T')[0],
        scheduledTime: '09:00',
        assignedTo: '',
        assignedToName: '',
        tags: [],
        thumbnailUrl: '',
        autoPublish: false,
        notes: '',
        contentType: 'video',
        checklist: []
      });
    }
    setCurrentStep(1);
    setErrors({});
  }, [content, isOpen]);

  const getPlatformIcon = (platform: PlatformName) => {
    const icons = {
      youtube: <Youtube className="w-5 h-5" />,
      instagram: <Instagram className="w-5 h-5" />,
      twitter: <Twitter className="w-5 h-5" />,
      linkedin: <Linkedin className="w-5 h-5" />,
      facebook: <Facebook className="w-5 h-5" />,
      tiktok: <div className="w-5 h-5 bg-black rounded-sm flex items-center justify-center text-white text-xs font-bold">T</div>,
      blog: <FileText className="w-5 h-5" />
    };
    return icons[platform];
  };

  const getContentTypeIcon = (type: ContentItem["contentType"]) => {
    const icons: Record<ContentItem["contentType"], JSX.Element> = {
      video: <Video className="w-4 h-4" />,
      image: <Image className="w-4 h-4" />,
      text: <FileText className="w-4 h-4" />,
      carousel: <Grid className="w-4 h-4" />,
      story: <Sparkles className="w-4 h-4" />,
    };
    return icons[type];
  };

  const validateStep = (step: number) => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!formData.title.trim()) newErrors.title = 'Title is required';
      if (!formData.description.trim()) newErrors.description = 'Description is required';
      if (!formData.platform) newErrors.platform = 'Platform is required';
    }

    if (step === 2) {
      if (!formData.scheduledDate) {
        newErrors.scheduledDate = 'Date is required';
      } else {
        const selectedDate = new Date(formData.scheduledDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) newErrors.scheduledDate = 'Date cannot be in the past';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleInputChange = <K extends keyof CreateContentItemData>(field: K, value: CreateContentItemData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const addTag = () => {
    if (newTag.trim() && !(formData.tags || []).includes(newTag.trim().toLowerCase())) {
      handleInputChange('tags', [...(formData.tags || []), newTag.trim().toLowerCase()]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    handleInputChange('tags', (formData.tags || []).filter(tag => tag !== tagToRemove));
  };

  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      const newItem = {
        id: `checklist_${Date.now()}`,
        text: newChecklistItem.trim(),
        completed: false
      };
      handleInputChange('checklist', [...(formData.checklist || []), newItem]);
      setNewChecklistItem('');
    }
  };

  const removeChecklistItem = (id: string) => {
    handleInputChange('checklist', (formData.checklist || []).filter(item => item.id !== id));
  };

  const toggleChecklistItem = (id: string) => {
    handleInputChange('checklist', (formData.checklist || []).map(item =>
      item.id === id ? { ...item, completed: !item.completed } : item
    ));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    try {
      if (content) {
        const contentData: UpdateContentItemData = {
          ...formData,
          id: content.id,
          scheduledDate: formData.scheduledDate,

        };
        await updateContent(content.id, contentData);
      } else {
        const contentData: CreateContentItemData = {
          ...formData,
          scheduledDate: formData.scheduledDate, // Already a string in ISO format
        };
        await createContent(contentData);
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to save content:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleTips = () => {
    setShowTips(prev => !prev);
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
          <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h4>
        <p className="text-gray-600 dark:text-gray-400 text-sm">Let's start with the essential details</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Platform</label>
        <div className="grid grid-cols-4 gap-3">
          {(['youtube', 'instagram', 'tiktok', 'twitter', 'linkedin', 'facebook', 'blog'] as PlatformName[]).map(platform => (
            <button
              key={platform}
              type="button"
              onClick={() => handleInputChange('platform', platform)}
              className={`p-3 rounded-lg border-2 transition-all duration-200 ${formData.platform === platform
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-500'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-white dark:bg-gray-800'
                }`}
            >
              <div className="flex flex-col items-center gap-2">
                {getPlatformIcon(platform)}
                <span className="text-xs font-medium capitalize text-gray-900 dark:text-gray-200">{platform}</span>
              </div>
            </button>
          ))}
        </div>
        {errors.platform && <p className="mt-1 text-sm text-red-600">{errors.platform}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Content Type</label>
        <div className="grid grid-cols-5 gap-3">
          {(['video', 'image', 'text', 'carousel', 'story'] as const).map(type => (
            <button
              key={type}
              type="button"
              onClick={() => handleInputChange('contentType', type)}
              className={`p-3 rounded-lg border-2 transition-all duration-200 ${formData.contentType === type
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-500'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-white dark:bg-gray-800'
                }`}
            >
              <div className="flex flex-col items-center gap-2">
                {getContentTypeIcon(type)}
                <span className="text-xs font-medium capitalize text-gray-900 dark:text-gray-200">{type}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${errors.title ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
            }`}
          placeholder="Enter your content title..."
        />
        {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description *</label>
        <textarea
          rows={4}
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${errors.description ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
            }`}
          placeholder="Describe your content..."
        />
        {errors.description && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description}</p>}
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{formData.description.length}/500 characters</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
        <select
          value={formData.status}
          onChange={(e) => handleInputChange('status', e.target.value as ScheduleStatus)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="draft">Draft</option>
          <option value="planning">Planning</option>
          <option value="ready">Ready</option>
          <option value="scheduled">Scheduled</option>
        </select>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
          <Calendar className="w-6 h-6 text-green-600 dark:text-green-400" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Schedule & Assignment</h4>
        <p className="text-gray-600 dark:text-gray-400 text-sm">When and who will handle this content</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Scheduled Date *</label>
          <input
            type="date"
            value={formData.scheduledDate}
            onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${errors.scheduledDate ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
              }`}
          />
          {errors.scheduledDate && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.scheduledDate}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Time</label>
          <input
            type="time"
            value={formData.scheduledTime}
            onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assign To</label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={formData.assignedToName}
            onChange={(e) => handleInputChange('assignedToName', e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            placeholder="Enter team member name..."
          />
        </div>
      </div>

      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">Auto Publish</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">Automatically publish when scheduled time arrives</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={formData.autoPublish}
            onChange={(e) => handleInputChange('autoPublish', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Quick Schedule</label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              handleInputChange('scheduledDate', tomorrow.toISOString().split('T')[0]);
              handleInputChange('scheduledTime', '09:00');
            }}
            className="p-3 text-left border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="font-medium text-gray-900 dark:text-white">Tomorrow Morning</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">9:00 AM</div>
          </button>

          <button
            type="button"
            onClick={() => {
              const nextWeek = new Date();
              nextWeek.setDate(nextWeek.getDate() + 7);
              handleInputChange('scheduledDate', nextWeek.toISOString().split('T')[0]);
              handleInputChange('scheduledTime', '14:00');
            }}
            className="p-3 text-left border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="font-medium text-gray-900 dark:text-white">Next Week</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">2:00 PM</div>
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Thumbnail URL</label>
        <div className="relative">
          <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="url"
            value={formData.thumbnailUrl}
            onChange={(e) => handleInputChange('thumbnailUrl', e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            placeholder="https://example.com/thumbnail.jpg"
          />
        </div>
        {formData.thumbnailUrl && (
          <div className="mt-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg">
            <div className="flex items-center gap-3">
              <img
                src={formData.thumbnailUrl}
                alt="Thumbnail preview"
                className="w-16 h-16 object-cover rounded-lg"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder-image.jpg';
                }}
              />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Thumbnail Preview</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Image loaded successfully</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Upload Media</h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">Drag and drop files here or click to browse</p>
        <button
          type="button"
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Choose Files
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
          <Hash className="w-6 h-6 text-purple-600 dark:text-purple-400" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Tags & Advanced Settings</h4>
        <p className="text-gray-600 dark:text-gray-400 text-sm">Organize your content and set goals</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Tags</label>
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="Add tag (e.g., #marketing #video)"
            />
            <button
              onClick={addTag}
              className="px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Hash className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(formData.tags || []).map(tag => (
              <span key={tag} className="inline-flex items-center px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 rounded-full text-sm">
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  className="ml-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Priority</label>
        <select
          value={formData.status}
          onChange={(e) => handleInputChange('status', e.target.value as ScheduleStatus)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Expected Reach</label>
        <input
          type="number"
          placeholder="Estimated audience reach"
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Engagement Goal</label>
        <input
          type="number"
          step="0.01"
          placeholder="e.g., 0.05 (5%)"
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
          <CheckCircle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Checklist & Notes</h4>
        <p className="text-gray-600 dark:text-gray-400 text-sm">Add tasks and additional information</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Checklist</label>
        <div className="space-y-2 mb-4">
          {(formData.checklist || []).map(item => (
            <div key={item.id} className="flex items-center gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg">
              <input
                type="checkbox"
                checked={item.completed}
                onChange={() => toggleChecklistItem(item.id)}
                className="h-4 w-4 text-primary-600 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 focus:ring-primary-500"
              />
              <span className={`flex-1 text-sm ${item.completed ? 'line-through text-gray-500 dark:text-gray-500' : 'text-gray-900 dark:text-gray-200'}`}>
                {item.text}
              </span>
              <button
                type="button"
                onClick={() => removeChecklistItem(item.id)}
                className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={newChecklistItem}
            onChange={(e) => setNewChecklistItem(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            placeholder="Add checklist item..."
          />
          <button
            type="button"
            onClick={addChecklistItem}
            className="px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notes</label>
        <textarea
          rows={6}
          value={formData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          placeholder="Add any additional notes or instructions..."
        />
      </div>

      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <h5 className="font-medium text-gray-900 dark:text-white mb-3">Content Summary</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Platform:</span>
            <span className="ml-2 font-medium capitalize text-gray-900 dark:text-white">{formData.platform}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Content Type:</span>
            <span className="ml-2 font-medium capitalize text-gray-900 dark:text-white">{formData.contentType}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Scheduled:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">{new Date(formData.scheduledDate).toLocaleDateString()} at {formData.scheduledTime}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Status:</span>
            <span className="ml-2 font-medium capitalize text-gray-900 dark:text-white">{formData.status}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Tags:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">{(formData.tags || []).length} tags</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Checklist:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">{(formData.checklist || []).length} items</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-purple-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold">
                {content ? 'Edit Content' : 'Add New Content'}
              </h3>
              <p className="text-primary-100 text-sm mt-1">
                Step {currentStep} of 4
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
              disabled={isSubmitting}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4 flex space-x-2">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`flex-1 h-2 rounded-full transition-colors ${step <= currentStep ? 'bg-white' : 'bg-white/30'
                  }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <CheckCircle className="w-4 h-4" />
            <span>All fields marked with * are required</span>
          </div>

          <div className="flex items-center gap-3">
            {(currentStep === 1 || currentStep === 2) && (
              <button
                onClick={toggleTips}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${showTips
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/50'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                title={showTips ? 'Hide Tips' : 'Show Tips'}
              >
                <Info className="w-4 h-4" />
                {showTips ? 'Hide Tips' : 'Show Tips'}
              </button>
            )}
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={isSubmitting}
              >
                Back
              </button>
            )}

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                disabled={isSubmitting}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {content ? 'Update Content' : 'Create Content'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Platform-specific tips */}
        {showTips && currentStep === 1 && (
          <div className="p-6 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-primary-900 dark:text-primary-100 mb-1">
                    {formData.platform === 'youtube' && 'YouTube Tips'}
                    {formData.platform === 'instagram' && 'Instagram Tips'}
                    {formData.platform === 'tiktok' && 'TikTok Tips'}
                    {formData.platform === 'twitter' && 'Twitter Tips'}
                    {formData.platform === 'linkedin' && 'LinkedIn Tips'}
                    {formData.platform === 'facebook' && 'Facebook Tips'}
                    {formData.platform === 'blog' && 'Blog Tips'}
                  </h4>
                  <div className="text-sm text-primary-800 dark:text-primary-200">
                    {formData.platform === 'youtube' && (
                      <ul className="space-y-1">
                        <li>• Keep titles under 60 characters for better visibility</li>
                        <li>• Include keywords early in your title</li>
                        <li>• Optimal posting time: 2-4 PM on weekdays</li>
                      </ul>
                    )}
                    {formData.platform === 'instagram' && (
                      <ul className="space-y-1">
                        <li>• Use 3-5 relevant hashtags in your caption</li>
                        <li>• Square images (1080x1080) work best for feed posts</li>
                        <li>• Best posting times: 11 AM - 1 PM and 7-9 PM</li>
                      </ul>
                    )}
                    {formData.platform === 'tiktok' && (
                      <ul className="space-y-1">
                        <li>• Keep videos between 15-30 seconds for better engagement</li>
                        <li>• Add trending sounds and hashtags</li>
                        <li>• Post between 6-10 AM and 7-9 PM</li>
                      </ul>
                    )}
                    {formData.platform === 'twitter' && (
                      <ul className="space-y-1">
                        <li>• Keep tweets concise and engaging</li>
                        <li>• Use 1-2 hashtags maximum</li>
                        <li>• Best times: 8-10 AM and 7-9 PM</li>
                      </ul>
                    )}
                    {formData.platform === 'linkedin' && (
                      <ul className="space-y-1">
                        <li>• Professional tone works best</li>
                        <li>• Include industry-relevant keywords</li>
                        <li>• Post during business hours: 9 AM - 5 PM</li>
                      </ul>
                    )}
                    {formData.platform === 'facebook' && (
                      <ul className="space-y-1">
                        <li>• Encourage engagement with questions</li>
                        <li>• Use high-quality images</li>
                        <li>• Post between 1-3 PM for best reach</li>
                      </ul>
                    )}
                    {formData.platform === 'blog' && (
                      <ul className="space-y-1">
                        <li>• Aim for 1,500+ words for better SEO</li>
                        <li>• Use header tags (H1, H2, H3) properly</li>
                        <li>• Include internal and external links</li>
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scheduling tips */}
        {showTips && currentStep === 2 && (
          <div className="p-6 bg-gray-50 border-t border-gray-200">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Target className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-green-900 mb-1">Optimal Scheduling</h4>
                  <div className="text-sm text-green-800">
                    <p className="mb-2">Based on your selected platform ({formData.platform}), here are the best times to post:</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="font-medium">Weekdays:</span>
                        <div className="text-xs mt-1">
                          {formData.platform === 'youtube' && '2:00 PM - 4:00 PM'}
                          {formData.platform === 'instagram' && '11:00 AM - 1:00 PM'}
                          {formData.platform === 'tiktok' && '6:00 AM - 10:00 AM'}
                          {formData.platform === 'twitter' && '8:00 AM - 10:00 AM'}
                          {formData.platform === 'linkedin' && '9:00 AM - 5:00 PM'}
                          {formData.platform === 'facebook' && '1:00 PM - 3:00 PM'}
                          {formData.platform === 'blog' && '9:00 AM - 11:00 AM'}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium">Weekends:</span>
                        <div className="text-xs mt-1">
                          {formData.platform === 'youtube' && '12:00 PM - 3:00 PM'}
                          {formData.platform === 'instagram' && '10:00 AM - 12:00 PM'}
                          {formData.platform === 'tiktok' && '7:00 PM - 9:00 PM'}
                          {formData.platform === 'twitter' && '9:00 AM - 11:00 AM'}
                          {formData.platform === 'linkedin' && 'Avoid weekends'}
                          {formData.platform === 'facebook' && '12:00 PM - 2:00 PM'}
                          {formData.platform === 'blog' && '2:00 PM - 4:00 PM'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddContentModal;